// 文件名: src/components/TD1501.js
import React, { useState, useMemo, useEffect } from 'react';
import { ArrowRight, Maximize, HelpCircle, CheckCircle, AlertTriangle, RefreshCw, Target, Move } from 'lucide-react';
import { useTranslation } from '../i18n';
import defaultConfig from '../config/td1501_params.json';
import PersonHeightControl from './common/PersonHeightControl';
import { calculateTopDownCoverage } from '../utils/TopDownCalc';
import { useUnit } from '../contexts/UnitContext';
import { formatLength, getDisplayValue, getMetersFromInput, getUnitLabel, getAreaUnitLabel } from '../utils/unitConversion';

const Tooltip = ({ explanation }) => (
  <div className="relative flex items-center group">
    <HelpCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 w-max max-w-xs p-2 text-sm text-white bg-gray-800 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
      {explanation}
    </div>
  </div>
);

const ParameterInput = ({ label, value, unit, onChange, tooltip, isEditable }) => {
    const finalIsEditable = !!onChange && isEditable;
    const inputClasses = finalIsEditable
        ? "bg-white"
        : "bg-gray-100 cursor-not-allowed";

    return (
        <div>
            <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
            {label} {tooltip && <Tooltip explanation={tooltip} />}
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
            <input
                type="number"
                value={value}
                onChange={onChange}
                className={`block w-full flex-1 rounded-none rounded-l-md border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm ${inputClasses}`}
                readOnly={!finalIsEditable}
            />
            <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">{unit}</span>
            </div>
        </div>
    );
};

const BooleanIndicator = ({ label, value, tooltip }) => {
    const { t } = useTranslation();
    return (
    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg h-full">
        <div className="flex items-center gap-1">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            {tooltip && <Tooltip explanation={tooltip} />}
        </div>
        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {value ? t('是') : t('否')}
        </span>
    </div>
)};

const degToRad = (degrees) => degrees * Math.PI / 180;
const radToDeg = (radians) => radians * 180 / Math.PI;

export const calculateTd1501Coverage = (inputs, paramsConfig, t) => {
  const { installationHeight, tiltingAngle, personHeight, mode, countingMethod = 'ReID', xTilt = 0, yTilt = 0 } = inputs;
  const {
    fovWidthAt3m, fovHeightAt3m, objectWidth, pixelWidth,
    recommendedPixel, personEntryCorrection, minCoverageDepth,
    dofNearL, dofFarL
  } = paramsConfig;

  const isTC = countingMethod === 'Traffic Only';

  if (mode === 'Top-Down' && !isTC) {
      const halfWidth1m = fovWidthAt3m / 6;
      const halfHeight1m = fovHeightAt3m / 6;
      const result = calculateTopDownCoverage({
          height: Math.max(0.1, installationHeight),
          personHeight,
          xTiltDeg: xTilt,
          yTiltDeg: yTilt,
          halfWidth1m,
          halfHeight1m
      });
      if (!result.isValid || !result.coverageField) {
          return { error: 'Invalid calculation range or angle too steep' };
      }
      return {
          error: null,
          isTopDown: true,
          details: result,
          summary: {
              D1: 0, W1: 0, D2: result.coverageField.aveHeight, W2: result.coverageField.aveWidth,
              suggestD: 0, suggestW: 0, area: result.coverageField.area, isValid: true, message: ""
          }
      };
  }

  // Dynamic parameters based on mode and counting method
  let maxTiltAngle, minPixel;
  if (isTC) {
    maxTiltAngle = 80;
    minPixel = 20;
  } else {
    maxTiltAngle = mode === 'Standard' ? 45 : paramsConfig.maxTiltAngle;
    minPixel = mode === 'Standard' ? 80 : paramsConfig.minPixel;
  }

  if (!minPixel || !fovWidthAt3m) return { error: t('error_incompleteParams')};

  // TC uses 2/3 body reference; ReID uses head reference
  const H_reference = isTC
    ? installationHeight - personHeight * 2 / 3
    : installationHeight - personHeight;
  if (H_reference <= 0) return { error: t('error_heightTooLow') };

  // For near-end (foot/body): TC uses H_reference; ReID uses full installationHeight
  const H_near = isTC ? H_reference : installationHeight;
  
  const verticalFov_deg = Math.atan(fovHeightAt3m / 2 / 3) * 2 * 180 / Math.PI;
  const verticalFov_rad = degToRad(verticalFov_deg);
  const pixelHeight = pixelWidth / 16 * 9;

  const calculatedMaxPixel = pixelWidth / 4;

  const W_pixel_min = objectWidth * pixelWidth / minPixel;
  const W_pixel_max = objectWidth * pixelWidth / calculatedMaxPixel;
  const W_pixel_rec = objectWidth * pixelWidth / recommendedPixel;

  const L_pixel_min = 3 * W_pixel_min / fovWidthAt3m;
  const L_pixel_max = 3 * W_pixel_max / fovWidthAt3m;
  const L_pixel_rec = 3 * W_pixel_rec / fovWidthAt3m;

  const D_pixel_min = L_pixel_min > H_reference ? Math.sqrt(L_pixel_min**2 - H_reference**2) : 0;
  const D_pixel_max = L_pixel_max > H_reference ? Math.sqrt(L_pixel_max**2 - H_reference**2) : 0;
  const D_pixel_rec = L_pixel_rec > H_reference ? Math.sqrt(L_pixel_rec**2 - H_reference**2) : 0;

  const D_view_far = (tiltingAngle + 0.00001) > 0 ? H_reference / Math.tan(degToRad(tiltingAngle + 0.00001)) : Infinity;
  const D_view_near = H_near / Math.tan(degToRad(tiltingAngle + 0.00001 + verticalFov_deg));
  const D_tilt_limit = H_near / Math.tan(degToRad(maxTiltAngle));

  const L_view_far = D_view_far !== Infinity ? Math.sqrt(D_view_far**2 + H_reference**2) : Infinity;
  const W_view_far = L_view_far !== Infinity ? L_view_far / 3 * fovWidthAt3m : Infinity;

  const L_view_near_for_width = Math.sqrt(D_view_near**2 + H_reference**2);
  const W_view_near = L_view_near_for_width / 3 * fovWidthAt3m;

  const L_tilt_limit_for_width = Math.sqrt(D_tilt_limit**2 + H_reference**2);
  const W_tilt_limit = L_tilt_limit_for_width / 3 * fovWidthAt3m;
    
  // DoF Calculations (ReID only, TC has no DoF constraints)
  const hasDoF = !isTC && (dofNearL || dofFarL);
  const L_dof_near = hasDoF ? (dofNearL || 0) : 0;
  const D_dof_near = L_dof_near > H_reference ? Math.sqrt(L_dof_near**2 - H_reference**2) : 0;
  const W_dof_near = (L_dof_near / 3) * fovWidthAt3m;

  const L_dof_far = hasDoF ? (dofFarL || Infinity) : Infinity;
  const D_dof_far = L_dof_far !== Infinity && L_dof_far > H_reference ? Math.sqrt(L_dof_far**2 - H_reference**2) : Infinity;
  const W_dof_far = L_dof_far !== Infinity ? (L_dof_far / 3) * fovWidthAt3m : Infinity;
  
  const theta_view_far = tiltingAngle;
  const theta_pixel_min = L_pixel_min > H_reference ? radToDeg(Math.asin(H_reference / L_pixel_min)) : 0;
  const theta_tilt_limit = maxTiltAngle;
  const theta_view_near = tiltingAngle + 0.00001 + verticalFov_deg;
  const theta_pixel_max = L_pixel_max > H_reference ? radToDeg(Math.asin(H_reference / L_pixel_max)) : 0;
  const theta_pixel_rec = L_pixel_rec > H_reference ? radToDeg(Math.asin(H_reference / L_pixel_rec)) : 0;
  const theta_dof_near = L_dof_near > H_reference ? radToDeg(Math.asin(H_reference / L_dof_near)) : 0;
  const theta_dof_far = L_dof_far !== Infinity && L_dof_far > H_reference ? radToDeg(Math.asin(H_reference / L_dof_far)) : 0;

  let D2_final, D1_final, W2_base, W1_base;
  if (isTC) {
    // TC: no DoF constraints
    D2_final = Math.min(D_view_far, D_pixel_min);
    D1_final = Math.max(D_tilt_limit, D_view_near, D_pixel_max);
    W2_base = Math.min(W_view_far, W_pixel_min);
    W1_base = Math.max(W_tilt_limit, W_view_near, W_pixel_max);
  } else {
    D2_final = Math.min(D_view_far, D_pixel_min, D_dof_far);
    D1_final = Math.max(D_tilt_limit, D_view_near, D_pixel_max, D_dof_near);
    W2_base = Math.min(W_view_far, W_pixel_min, W_dof_far);
    W1_base = Math.max(W_tilt_limit, W_view_near, W_pixel_max, W_dof_near);
  }
  
  const W2_final = W2_base - personEntryCorrection;
  const W1_final = W1_base - personEntryCorrection;
  
  const unclampedRecommendedD = D_pixel_rec;
  const unclampedRecommendedW = W_pixel_rec - personEntryCorrection;

  const isValid = D2_final - D1_final >= minCoverageDepth;
  const isRecommended = unclampedRecommendedD > D1_final && unclampedRecommendedD < D2_final;

  const suggestD = isValid ? Math.min(Math.max(unclampedRecommendedD, D1_final), D2_final) : 0;
  const suggestW = isValid ? Math.min(Math.max(unclampedRecommendedW, W1_final), W2_final) : 0;

  let message = "";
  if (!isValid) {
      message = t('error_configInvalid', { minCoverageDepth });
  } else if (D1_final > D2_final) {
      message = t('error_d1_gt_d2');
  }
  
  const finalD1 = isValid ? D1_final : 0;
  const finalD2 = isValid ? D2_final : 0;
  const finalW1 = isValid && W1_final > 0 ? W1_final : 0;
  const finalW2 = isValid && W2_final > 0 ? W2_final : 0;

  const area = isValid ? (finalW1 + finalW2) * (finalD2 - finalD1) / 2 : 0;
  
  // Build details arrays - TC has 6 points (no DoF), ReID has 8
  let D_for_details, L_for_details, W_for_details, theta_for_details, labels_for_details;
  if (isTC) {
    D_for_details = [D_view_far, D_pixel_min, D_tilt_limit, D_view_near, D_pixel_max, D_pixel_rec];
    L_for_details = [L_view_far, L_pixel_min, L_tilt_limit_for_width, L_view_near_for_width, L_pixel_max, L_pixel_rec];
    W_for_details = [W_view_far, W_pixel_min, W_tilt_limit, W_view_near, W_pixel_max, W_pixel_rec];
    theta_for_details = [theta_view_far, theta_pixel_min, theta_tilt_limit, theta_view_near, theta_pixel_max, theta_pixel_rec];
    labels_for_details = [t('point_view_far_body'), t('point_pixel_min_body'), t('point_tilt_limit_body'), t('point_view_near_body'), t('point_pixel_max_body'), t('point_pixel_rec_body')];
  } else {
    D_for_details = [D_view_far, D_pixel_min, D_tilt_limit, D_view_near, D_pixel_max, D_pixel_rec, D_dof_near, D_dof_far];
    L_for_details = [L_view_far, L_pixel_min, L_tilt_limit_for_width, L_view_near_for_width, L_pixel_max, L_pixel_rec, L_dof_near, L_dof_far];
    W_for_details = [W_view_far, W_pixel_min, W_tilt_limit, W_view_near, W_pixel_max, W_pixel_rec, W_dof_near, W_dof_far];
    theta_for_details = [theta_view_far, theta_pixel_min, theta_tilt_limit, theta_view_near, theta_pixel_max, theta_pixel_rec, theta_dof_near, theta_dof_far];
    labels_for_details = [t('point_view_far'), t('point_pixel_min'), t('point_tilt_limit'), t('point_view_near'), t('point_pixel_max'), t('point_pixel_rec'), t('point_dof_near'), t('point_dof_far')];
  }

  return {
    error: null,
    isTC,
    base: { H_lens_to_head: H_reference, verticalFov: verticalFov_rad, pixelHeight, calculatedMaxPixel },
    details: {
        labels: labels_for_details,
        L: L_for_details,
        D: D_for_details,
        theta: theta_for_details,
        W: W_for_details,
    },
    summary: {
        D1: finalD1, W1: finalW1, D2: finalD2, W2: finalW2,
        suggestD, 
        suggestW,
        recommendedD: unclampedRecommendedD,
        recommendedW: unclampedRecommendedW,
        area, isValid, isRecommended, message,
    }
  };
};

export const findOptimalAngle = (inputs, params, t) => {
    let maxArea = 0;
    let optimalAngle = 0;
    let foundValid = false;

    for (let angle = 0; angle <= 90; angle += 1) {
        const tempInputs = { ...inputs, tiltingAngle: angle };
        const result = calculateTd1501Coverage(tempInputs, params, t);
        
        if (result && result.summary && result.summary.isValid) {
            foundValid = true;
            if (result.summary.area >= maxArea) {
                maxArea = result.summary.area;
                optimalAngle = angle;
            }
        }
    }
    return foundValid ? optimalAngle : 0;
};

export const findMaxValidAngle = (inputs, params, t, optimalAngle) => {
    let maxValidAngle = optimalAngle;
    for (let angle = optimalAngle; angle <= 90; angle += 1) {
        const tempInputs = { ...inputs, tiltingAngle: angle };
        const result = calculateTd1501Coverage(tempInputs, params, t);
        if (result && result.summary && result.summary.isValid) {
            maxValidAngle = angle;
        } else {
            break;
        }
    }
    return maxValidAngle;
};

const Controls = ({ inputs, setInputs, lensPresets, lensPresetsTC, lensModes, lensModesTC, optimalAngle, maxValidAngle, isAdmin, dynamicHeightBounds }) => {
  const { t } = useTranslation();
  const { unitSystem } = useUnit();
  
  const [warningModal, setWarningModal] = useState({ isOpen: false, targetMode: null });
  const [dontShowAgain, setDontShowAgain] = useState(false);
  
  const isTC = inputs.countingMethod === 'Traffic Only';
  const activeLensPresets = isTC ? lensPresetsTC : lensPresets;
  const activeLensModes = isTC ? lensModesTC : lensModes;
  
  const currentModeOptions = activeLensModes[inputs.lensType] || {};
  const selectedModeConfig = currentModeOptions[inputs.mode] || { heightRange: [1.5, 10] };
  const [minHeight, maxHeight] = dynamicHeightBounds || selectedModeConfig.heightRange;
  const displayMinHeight = getDisplayValue(minHeight, unitSystem);
  const displayMaxHeight = getDisplayValue(maxHeight, unitSystem);

  const handleInputChange = (key, value, isFloat = false) => {
    let newInputs = { ...inputs, [key]: isFloat ? parseFloat(value) : value };
  
    if (key === 'countingMethod') {
      const newIsTC = value === 'Traffic Only';
      const newPresets = newIsTC ? lensPresetsTC : lensPresets;
      const newModes = newIsTC ? lensModesTC : lensModes;
      
      // If current lens not available in new presets, pick first
      if (!newPresets[newInputs.lensType]) {
        newInputs.lensType = Object.keys(newPresets)[0];
      }
      
      // If current mode not available, pick first
      const availableModes = Object.keys(newModes[newInputs.lensType] || {});
      if (!availableModes.includes(newInputs.mode)) {
        newInputs.mode = availableModes[0] || 'Standard';
      }
      
      // Clamp height
      const modeConfig = newModes[newInputs.lensType]?.[newInputs.mode];
      if (modeConfig) {
        const [newMin, newMax] = modeConfig.heightRange;
        if (newInputs.installationHeight < newMin) newInputs.installationHeight = newMin;
        if (newInputs.installationHeight > newMax) newInputs.installationHeight = newMax;
      }
    }

    if (key === 'lensType' || key === 'mode') {
      let updatedMode = key === 'mode' ? value : newInputs.mode;
      const targetLens = key === 'lensType' ? value : newInputs.lensType;
      const currentModes = activeLensModes[targetLens] || {};
      
      const availableModesForTargetLens = Object.keys(currentModes);
      if (!availableModesForTargetLens.includes(updatedMode)) {
        updatedMode = availableModesForTargetLens[0];
        newInputs.mode = updatedMode;
      }
      
      const newModeConfig = currentModes[updatedMode];
      if (newModeConfig) {
        const [newMin, newMax] = newModeConfig.heightRange;
        let currentHeight = newInputs.installationHeight;
        if (currentHeight < newMin) currentHeight = newMin;
        else if (currentHeight > newMax) currentHeight = newMax;
        newInputs.installationHeight = currentHeight;
      }
    }
    
    setInputs(newInputs);
  };

  const handleCountingMethodChange = (newMethod) => {
    if (newMethod === 'Traffic Only') {
      const storageKey = 'hideWarning_TrafficOnly';
      const hideWarning = localStorage.getItem(storageKey);
      const now = new Date().getTime();
      let shouldShow = false;
      
      if (hideWarning) {
        const parsed = JSON.parse(hideWarning);
        if (now > parsed.expiry) { localStorage.removeItem(storageKey); shouldShow = true; }
      } else { shouldShow = true; }

      if (shouldShow) {
        setWarningModal({ isOpen: true, targetMode: 'TrafficOnly' });
      } else {
        handleInputChange('countingMethod', newMethod);
      }
    } else {
      handleInputChange('countingMethod', newMethod);
    }
  };

  const handleModeDropdownChange = (newMode) => {
      const storageKey = `hideWarning_${newMode}`;
      const hideWarning = localStorage.getItem(storageKey);
      
      const now = new Date().getTime();
      let shouldShow = false;
      
      if (hideWarning) {
          const parsed = JSON.parse(hideWarning);
          if (now > parsed.expiry) { localStorage.removeItem(storageKey); shouldShow = true; }
      } else { shouldShow = true; }

      if ((newMode === 'Wide Field' || newMode === 'Top-Down') && shouldShow) {
          setWarningModal({ isOpen: true, targetMode: newMode });
      } else {
          handleInputChange('mode', newMode);
      }
  };

  const confirmModeChange = () => {
      if (dontShowAgain) {
          const targetKey = warningModal.targetMode === 'TrafficOnly' ? 'TrafficOnly' : warningModal.targetMode;
          const storageKey = `hideWarning_${targetKey}`;
          const expiry = new Date().getTime() + 15 * 24 * 60 * 60 * 1000;
          localStorage.setItem(storageKey, JSON.stringify({ expiry }));
      }
      if (warningModal.targetMode === 'TrafficOnly') {
          handleInputChange('countingMethod', 'Traffic Only');
      } else {
          handleInputChange('mode', warningModal.targetMode);
      }
      setWarningModal({ isOpen: false, targetMode: null });
      setDontShowAgain(false);
  };

  const cancelModeChange = () => {
      setWarningModal({ isOpen: false, targetMode: null });
      setDontShowAgain(false);
  };

  const getWarningTitle = () => {
    if (warningModal.targetMode === 'TrafficOnly') return t('warning_trafficOnlyTitle');
    if (warningModal.targetMode === 'Wide Field') return t('warning_wideFieldTitle');
    return t('warning_topDownTitle');
  };
  const getWarningText = () => {
    if (warningModal.targetMode === 'TrafficOnly') return t('warning_trafficOnlyText');
    if (warningModal.targetMode === 'Wide Field') return t('warning_wideFieldText');
    return t('warning_topDownText');
  };

  return (
    <div className={`p-6 rounded-lg shadow-lg ${isTC ? 'bg-amber-50 border border-amber-200' : 'bg-white'}`}>
      <h2 className="text-xl font-bold mb-4 text-gray-700">{t('controlsTitle')}</h2>
      <div className="space-y-6">
        {/* Counting Method Selector */}
        <div>
          <label htmlFor="countingMethod" className="block text-sm font-medium text-gray-600 mb-1 flex items-center gap-1">{t('countingMethod')} <Tooltip explanation={t('tooltip_countingMethod')} /></label>
          <select id="countingMethod" value={inputs.countingMethod} onChange={(e) => handleCountingMethodChange(e.target.value)}
            className={`w-full p-2 border rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 font-medium ${isTC ? 'border-amber-400 bg-amber-100 text-amber-800' : 'border-gray-300'}`}>
            <option value="ReID">{t('countingMethodReID')}</option>
            <option value="Traffic Only">{t('countingMethodTrafficOnly')}</option>
          </select>
        </div>
        {/* Traffic Only Badge */}
        {isTC && (
          <div className="flex items-start gap-2 p-3 bg-amber-100 border border-amber-300 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">{t('trafficOnlyBadge')}</p>
              <p className="text-xs text-amber-700 mt-0.5">{t('trafficOnlyBadgeDesc')}</p>
            </div>
          </div>
        )}
        {/* Lens Model */}
        <div>
          <label htmlFor="lensType" className="block text-sm font-medium text-gray-600 mb-1">{t('lensModel')}</label>
          <select id="lensType" value={inputs.lensType} onChange={(e) => handleInputChange('lensType', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500">
            {Object.keys(activeLensPresets).map(lens => <option key={lens} value={lens}>{lens}</option>)}
          </select>
        </div>
        {/* Vision Mode */}
        <div>
          <label htmlFor="modeSelect" className="block text-sm font-medium text-gray-600 mb-1 flex items-center gap-1">Mode <Tooltip explanation={t('tooltip_td1501Mode')} /></label>
          <select id="modeSelect" value={inputs.mode} onChange={(e) => handleModeDropdownChange(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500">
            {Object.keys(currentModeOptions).map(modeKey => (
              <option key={modeKey} value={modeKey}>{modeKey}</option>
            ))}
          </select>
        </div>
        {/* Installation Height */}
        <div>
          <label htmlFor="installationHeight" className="block text-sm font-medium text-gray-600 mb-1">
            {t('installationHeight')}: <span className={`font-bold ${isTC ? 'text-amber-600' : 'text-cyan-600'}`}>{formatLength(inputs.installationHeight, unitSystem, 1)}</span>
            <span className="text-xs ml-2 text-gray-500">({t('range')}: {formatLength(minHeight, unitSystem, 1)} - {formatLength(maxHeight, unitSystem, 1)})</span>
          </label>
          <input type="range" id="installationHeight" min={displayMinHeight} max={displayMaxHeight} step={unitSystem === 'imperial' ? "0.5" : "0.1"} value={getDisplayValue(inputs.installationHeight, unitSystem)}
            onChange={(e) => handleInputChange('installationHeight', getMetersFromInput(e.target.value, unitSystem), false)} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
        </div>
        {/* Tilting Angle / XY Tilt */}
        {inputs.mode !== 'Top-Down' ? (
            <div>
              <label htmlFor="tiltingAngle" className="block text-sm font-medium text-gray-600 mb-1">
                {t('tiltingAngle')}: <span className={`font-bold ${isTC ? 'text-amber-600' : 'text-cyan-600'}`}>{inputs.tiltingAngle.toFixed(0)} °</span>
                <span className="text-sm text-gray-500 ml-2">({t('optimal')}: {optimalAngle}°, {t('range')}: {optimalAngle}° - {maxValidAngle}°)</span>
              </label>
              <input type="range" id="tiltingAngle" min={optimalAngle} max={maxValidAngle} step="1" value={inputs.tiltingAngle}
                onChange={(e) => handleInputChange('tiltingAngle', parseInt(e.target.value, 10))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
            </div>
        ) : (
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('xTilt')}: <span className="font-bold text-cyan-600">{inputs.xTilt || 0} °</span>
                    </label>
                    <input type="range" min="-15" max="15" step="1" value={inputs.xTilt || 0}
                        onChange={(e) => handleInputChange('xTilt', e.target.value, false)}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('yTilt')}: <span className="font-bold text-cyan-600">{inputs.yTilt || 0} °</span>
                    </label>
                    <input type="range" min="-15" max="15" step="1" value={inputs.yTilt || 0}
                        onChange={(e) => handleInputChange('yTilt', e.target.value, false)}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                </div>
            </div>
        )}
        <div>
            <PersonHeightControl
                personHeight={inputs.personHeight}
                onPersonHeightChange={(newHeight) => {
                    setInputs(prev => ({ ...prev, personHeight: newHeight }));
                }}
                onPersonHeightInput={(newHeight) => setInputs(prev => ({...prev, personHeight: newHeight}))}
                isAdmin={isAdmin}
            />
        </div>
      </div>
      
      {/* Warning Modal */}
      {warningModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex items-center gap-3 mb-4 text-amber-500">
                    <AlertTriangle className="w-6 h-6" />
                    <h3 className="text-lg font-bold text-gray-800">
                        {getWarningTitle()}
                    </h3>
                </div>
                <p className="text-gray-600 mb-6 whitespace-pre-line leading-relaxed">
                    {getWarningText()}
                </p>
                <div className="flex items-center gap-2 mb-6 cursor-pointer" onClick={() => setDontShowAgain(!dontShowAgain)}>
                    <div className={`w-5 h-5 flex items-center justify-center border rounded ${dontShowAgain ? 'bg-cyan-600 border-cyan-600' : 'border-gray-300'}`}>
                        {dontShowAgain && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-sm text-gray-600 select-none">{t('doNotRemind15Days')}</span>
                </div>
                <div className="flex justify-end gap-3">
                    <button onClick={cancelModeChange} className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors">
                        Cancel
                    </button>
                    <button onClick={confirmModeChange} className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700 transition-colors">
                        {t('iUnderstand')}
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};



const ParametersAndDerivedPanel = ({ data, params, setParams, onReset, isAdmin }) => {
    const [message, setMessage] = useState('');
    const { t } = useTranslation();
    const { unitSystem } = useUnit();

    const handleParamChange = (key, value) => {
        setParams(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
    };

    const handleMeterParamChange = (key, displayValue) => {
        setParams(prev => ({ ...prev, [key]: getMetersFromInput(parseFloat(displayValue) || 0, unitSystem) }));
    };

    const showMessage = (text) => {
        setMessage({ text });
        setTimeout(() => setMessage(''), 3000);
    };

    const handleReset = () => {
        onReset();
        showMessage(t('resetSuccess'));
    };

    if (!data || !data.base) return null;

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-700">{t('paramsTitle')}</h2>
                {isAdmin && (
                    <button 
                        onClick={handleReset}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-500 text-white text-sm font-semibold rounded-md hover:bg-gray-600 transition-colors"
                        title="Clear saved settings in browser and restore defaults"
                    >
                        <RefreshCw className="w-4 h-4"/>
                        {t('reset')}
                    </button>
                )}
            </div>
            {message && 
                <div className="mb-4 text-sm p-3 rounded-md text-green-700 bg-green-100">
                    {message.text}
                </div>
            }
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <ParameterInput label={t('personHeight')} value={getDisplayValue(params.personHeight, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} onChange={(e) => handleMeterParamChange('personHeight', e.target.value)} isEditable={isAdmin} />
                <ParameterInput label={t('lensToHead')} value={getDisplayValue(data.base.H_lens_to_head, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} tooltip={`${t('installationHeight')} - ${t('personHeight')}`} isEditable={false} />
                <ParameterInput label={t('shoulderWidth')} value={getDisplayValue(params.objectWidth, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} onChange={(e) => handleMeterParamChange('objectWidth', e.target.value)} isEditable={isAdmin} />
                <ParameterInput label={t('personEntryCorrection')} value={getDisplayValue(params.personEntryCorrection, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} onChange={(e) => handleMeterParamChange('personEntryCorrection', e.target.value)} tooltip="Correction value subtracted from calculated FoV width to ensure full body entry." isEditable={isAdmin} />
                <ParameterInput label={t('pixelWidth')} value={params.pixelWidth} unit="px" onChange={(e) => handleParamChange('pixelWidth', e.target.value)} isEditable={isAdmin} />
                <ParameterInput label={t('pixelHeight')} value={data.base.pixelHeight.toFixed(0)} unit="px" tooltip={`${t('pixelWidth')} / 16 * 9`} isEditable={false} />
                <ParameterInput label={t('fovWidth3m')} value={getDisplayValue(params.fovWidthAt3m, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} onChange={(e) => handleMeterParamChange('fovWidth3m', e.target.value)} isEditable={isAdmin} />
                <ParameterInput label={t('fovHeight3m')} value={getDisplayValue(params.fovHeightAt3m, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} onChange={(e) => handleMeterParamChange('fovHeight3m', e.target.value)} isEditable={isAdmin} />
                <ParameterInput label={t('verticalFov')} value={radToDeg(data.base.verticalFov).toFixed(2)} unit="°" tooltip={`Calculated from ${t('fovHeight3m')}`} isEditable={false} />
                <ParameterInput label={t('maxTiltAngle')} value={params.maxTiltAngle} unit="°" onChange={(e) => handleParamChange('maxTiltAngle', e.target.value)} isEditable={isAdmin} />
                <ParameterInput label={t('minBodyPixel')} value={params.minPixel} unit="px" onChange={(e) => handleParamChange('minPixel', e.target.value)} isEditable={isAdmin} />
                <ParameterInput label={t('maxBodyPixel')} value={data.base.calculatedMaxPixel.toFixed(0)} unit="px" tooltip={`Calculated as ${t('pixelWidth')} / 4`} isEditable={false} />
                <ParameterInput label={t('recBodyPixel')} value={params.recommendedPixel} unit="px" onChange={(e) => handleParamChange('recommendedPixel', e.target.value)} isEditable={isAdmin} />
                <ParameterInput label={t('minEffectiveDepth')} value={getDisplayValue(params.minCoverageDepth, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} onChange={(e) => handleMeterParamChange('minCoverageDepth', e.target.value)} isEditable={isAdmin} />
                <ParameterInput label={t('recommendedD')} value={getDisplayValue(data.summary.recommendedD, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} tooltip="Original recommended distance based on 'Rec. Body Pixel', not clamped by actual coverage." isEditable={false} />
                <ParameterInput label={t('recommendedW')} value={getDisplayValue(data.summary.recommendedW, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} tooltip="Original recommended width based on 'Rec. Body Pixel', not clamped by actual coverage." isEditable={false} />
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                     <BooleanIndicator 
                        label={t('isRecommendedInView')}
                        value={data.summary.isRecommended} 
                        tooltip={t('tooltip_isRecommendedInView')}
                    />
                    <BooleanIndicator 
                        label={t('isDepthValid', {minCoverageDepth: params.minCoverageDepth})}
                        value={data.summary.isValid}
                        tooltip={t('tooltip_isDepthValid')}
                    />
                </div>
            </div>
        </div>
    );
};

const DetailedResultsTable = ({ data }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!data || !data.details) return null;
    const { labels, L, D, theta, W } = data.details;
    const formatLengthCell = (val) => (val === Infinity ? '∞' : getDisplayValue(val, unitSystem).toFixed(2));
    const formatDeg = (val) => (val === Infinity ? '∞' : val.toFixed(2));

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('detailedTableTitle')}</h2>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table_param')}</th>
                            {labels.map(label => <th key={label} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</th>)}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                            <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{t('table_l')} ({getUnitLabel(unitSystem)})</td>
                            {L.map((val, i) => <td key={i} className="px-2 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{formatLengthCell(val)}</td>)}
                        </tr>
                        <tr>
                            <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{t('table_d')} ({getUnitLabel(unitSystem)})</td>
                            {D.map((val, i) => <td key={i} className="px-2 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{formatLengthCell(val)}</td>)}
                        </tr>
                        <tr>
                            <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{t('table_theta')}</td>
                            {theta.map((val, i) => <td key={i} className="px-2 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{formatDeg(val)}</td>)}
                        </tr>
                        <tr>
                            <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{t('table_w')} ({getUnitLabel(unitSystem)})</td>
                            {W.map((val, i) => <td key={i} className="px-2 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{formatLengthCell(val)}</td>)}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const SummaryResults = ({ data }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!data || !data.summary) return null;
    const { D1, W1, D2, W2, suggestD, suggestW, area, isValid, message } = data.summary;
    
    const ResultCard = ({ title, value, unit, icon }) => (
        <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-4">
          {icon}
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-xl font-bold text-gray-800">{value} <span className="text-sm font-normal">{unit}</span></p>
          </div>
        </div>
      );

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('summaryTitle')}</h2>
            {message && (
                <div className={`mb-4 p-3 border-l-4 rounded-md flex items-center gap-3 ${isValid ? 'bg-yellow-100 border-yellow-500 text-yellow-700' : 'bg-red-100 border-red-500 text-red-700'}`}>
                    <AlertTriangle className="w-5 h-5"/>
                    <p>{message}</p>
                </div>
            )}
            {data.isTopDown ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <ResultCard title={t('effectiveWidth')} value={getDisplayValue(W2, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} icon={<Maximize className="w-8 h-8 text-blue-500" />} />
                    <ResultCard title={t('effectiveDepth')} value={getDisplayValue(D2, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} icon={<ArrowRight className="w-8 h-8 text-red-500 transform rotate-90" />} />
                    <ResultCard title={t('coverageArea')} value={getDisplayValue(area, unitSystem).toFixed(2)} unit={getAreaUnitLabel(unitSystem)} icon={<Target className="w-8 h-8 text-purple-500" />} />
                    <ResultCard title="Bounds Offset" value={`${getDisplayValue(data.details?.coverageField?.width - W2, unitSystem).toFixed(2)}`} unit={`${getUnitLabel(unitSystem)} skew`} icon={<Move className="w-8 h-8 text-orange-500" />} />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ResultCard title={t('nearPoint')} value={`${getDisplayValue(D1, unitSystem).toFixed(2)} / ${getDisplayValue(W1, unitSystem).toFixed(2)}`} unit={getUnitLabel(unitSystem)} icon={<ArrowRight className="w-8 h-8 text-blue-500" />} />
                    <ResultCard title={t('farPoint')} value={`${getDisplayValue(D2, unitSystem).toFixed(2)} / ${getDisplayValue(W2, unitSystem).toFixed(2)}`} unit={getUnitLabel(unitSystem)} icon={<ArrowRight className="w-8 h-8 text-red-500" />} />
                    <ResultCard title={t('suggestedPoint')} value={`${getDisplayValue(suggestD, unitSystem).toFixed(2)} / ${getDisplayValue(suggestW, unitSystem).toFixed(2)}`} unit={getUnitLabel(unitSystem)} icon={<CheckCircle className="w-8 h-8 text-green-500" />} />
                    <ResultCard title={t('coverageArea')} value={getDisplayValue(area, unitSystem).toFixed(2)} unit={getAreaUnitLabel(unitSystem)} icon={<Maximize className="w-8 h-8 text-purple-500" />} />
                </div>
            )}
        </div>
    );
};

const CoverageDiagram = ({ inputs, results, params }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!results || !results.summary) return null;

    if (results.isTopDown) {
        const { installationHeight, yTilt = 0 } = inputs;
        const vf = results.details?.visionField;
        const cf = results.details?.coverageField;
        if (!vf || !cf) return null;

        const width = 500, height = 350, padding = 40;
        
        const yCoordsCoverage = cf.polygon.map(p => p.y);
        const yCoordsVision = vf.polygon.map(p => p.y);
        const minY = Math.min(...yCoordsCoverage);
        const maxY = Math.max(...yCoordsCoverage);
        const visionMinY = Math.min(...yCoordsVision);
        const visionMaxY = Math.max(...yCoordsVision);
        
        const maxAbsY = Math.max(Math.abs(visionMinY), Math.abs(visionMaxY));
        const horizontalSpan = maxAbsY * 2;
        
        const scaleX = horizontalSpan > 0 ? (width - padding * 2) / horizontalSpan : 1;
        const scaleY = installationHeight > 0 ? (height - padding * 2.5) / installationHeight : 1;
        const scale = Math.min(scaleX, scaleY);
        
        const groundY = height - padding * 2;
        const camX = width / 2;
        const camY = groundY - installationHeight * scale;
        
        const visionStartPx = camX + visionMinY * scale;
        const visionEndPx = camX + visionMaxY * scale;
        const coverageStartPx = camX + minY * scale;
        const coverageEndPx = camX + maxY * scale;

        return (
            <div className="bg-white p-6 rounded-lg shadow-lg h-full">
                <h2 className="text-xl font-bold mb-4 text-gray-700">{t('sideView')} (Y-Z)</h2>
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto border rounded-md">
                    <defs>
                        <marker id="arrowhead-td1501-side" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" /></marker>
                    </defs>
                    <line x1="0" y1={groundY} x2={width} y2={groundY} stroke="#9ca3af" strokeWidth="1" />
                    <text x={width - 5} y={groundY - 5} fontSize="10" fill="#6b7280" textAnchor="end">{t('ground')}</text>
                    
                    <line x1={camX} y1={camY} x2={camX} y2={groundY} stroke="#9ca3af" strokeWidth="1" strokeDasharray="4" />
                    <text x={camX + 5} y={camY} fontSize="10" fill="#6b7280">H: {formatLength(installationHeight, unitSystem, 1)}</text>
                    
                    <polygon points={`${camX},${camY} ${visionEndPx},${groundY} ${visionStartPx},${groundY}`} fill="rgba(0, 180, 255, 0.1)" />
                    <line x1={camX} y1={camY} x2={visionEndPx} y2={groundY} stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                    <line x1={camX} y1={camY} x2={visionStartPx} y2={groundY} stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                    
                    <rect x={coverageStartPx} y={groundY - 5} width={coverageEndPx - coverageStartPx} height="10" fill="rgba(22, 163, 74, 0.7)" />
                    
                    <g transform={`translate(${camX}, ${camY}) rotate(${yTilt})`}>
                        <rect x="-10" y="-6" width="20" height="12" rx="2" fill="#0891b2" />
                        <circle cx="0" cy="0" r="4" fill="white" />
                    </g>
                    
                    <line x1={coverageStartPx} y1={groundY + 20} x2={coverageEndPx} y2={groundY + 20} stroke="#3b82f6" strokeWidth="1" markerStart="url(#arrowhead-td1501-side)" markerEnd="url(#arrowhead-td1501-side)"/>
                    <text x={coverageStartPx + (coverageEndPx - coverageStartPx)/2} y={groundY + 33} fontSize="10" fill="#3b82f6" textAnchor="middle">{t('effectiveDepth')}: {formatLength(maxY-minY, unitSystem, 2)}</text>
                </svg>
            </div>
        );
    }

    if (!results.base) return null;

    const { installationHeight, tiltingAngle, personHeight } = inputs;
    const { D1, D2, suggestD, isValid } = results.summary;
    const { H_lens_to_head } = results.base;

    const width = 500;
    const height = 350;
    const padding = 40;

    const scaleY = (height - padding * 2.5) / Math.max(5, installationHeight);
    const scaleX = D2 > 0 ? (width - padding * 2) / D2 : scaleY;
    const scale = Math.min(scaleX, scaleY);
    
    const groundY = height - padding * 2;
    const camX = padding;
    const camY = groundY - installationHeight * scale;

    const d1_px = camX + D1 * scale;
    const d2_px = camX + D2 * scale;
    const suggestD_px = camX + suggestD * scale;
    
    const angleTop = isValid && D2 > 0 ? radToDeg(Math.atan(H_lens_to_head / D2)) : tiltingAngle;
    const angleBottom = isValid && D1 > 0 ? radToDeg(Math.atan(installationHeight / D1)) : tiltingAngle;

    const lineLength = width * 2;
    const topRayX = camX + lineLength * Math.cos(degToRad(angleTop));
    const topRayY = camY + lineLength * Math.sin(degToRad(angleTop));
    const bottomRayX = camX + lineLength * Math.cos(degToRad(angleBottom));
    const bottomRayY = camY + lineLength * Math.sin(degToRad(angleBottom));

    const arcRadius = 30;
    const arcStartX = camX + arcRadius;
    const arcStartY = camY;
    const arcEndX = camX + arcRadius * Math.cos(degToRad(tiltingAngle));
    const arcEndY = camY + arcRadius * Math.sin(degToRad(tiltingAngle));
    const largeArcFlag = tiltingAngle <= 180 ? "0" : "1";

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg h-full">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('sideView')}</h2>
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto border rounded-md">
                <defs>
                    <marker id="arrowhead-td1501" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
                    </marker>
                </defs>
                <line x1="0" y1={groundY} x2={width} y2={groundY} stroke="#9ca3af" strokeWidth="1" />
                <text x={width-5} y={groundY-5} fontSize="10" fill="#6b7280" textAnchor="end">{t('ground')}</text>
                <line x1={camX} y1={camY} x2={camX} y2={groundY} stroke="#9ca3af" strokeWidth="1" strokeDasharray="4" />
                <text x={camX + 5} y={camY - 10} fontSize="10" fill="#6b7280">H: {formatLength(installationHeight, unitSystem, 1)}</text>
                <polygon points={`${camX},${camY} ${topRayX},${topRayY} ${bottomRayX},${bottomRayY}`} fill="rgba(0, 180, 255, 0.1)" />
                <line x1={camX} y1={camY} x2={topRayX} y2={topRayY} stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                <line x1={camX} y1={camY} x2={bottomRayX} y2={bottomRayY} stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                {isValid && (
                    <rect x={d1_px} y={groundY - 5} width={d2_px - d1_px} height="10" fill="rgba(22, 163, 74, 0.7)" />
                )}
                 {isValid && (
                    <g>
                        <line x1={suggestD_px} y1={groundY - 10} x2={suggestD_px} y2={groundY + 10} stroke="#22c55e" strokeWidth="2" strokeDasharray="4,2" />
                        <text x={suggestD_px} y={groundY - 15} fontSize="10" fill="#22c55e" textAnchor="middle">{t('suggestedPoint').split(' ')[0]}</text>
                    </g>
                )}
                <g transform={`translate(${camX}, ${camY}) rotate(${tiltingAngle})`}>
                    <rect x="-10" y="-6" width="20" height="12" rx="2" fill="#0891b2" />
                    <circle cx="0" cy="0" r="4" fill="white" />
                </g>
                <g>
                    <line x1={camX} y1={camY} x2={camX + arcRadius + 20} y2={camY} stroke="#f59e0b" strokeWidth="1" strokeDasharray="2,2"/>
                    <path d={`M ${arcStartX} ${arcStartY} A ${arcRadius} ${arcRadius} 0 ${largeArcFlag} 1 ${arcEndX} ${arcEndY}`} stroke="#f59e0b" strokeWidth="1.5" fill="none"/>
                    <text x={camX + arcRadius + 5} y={camY + 15} fontSize="10" fill="#f59e0b">θ: {tiltingAngle}°</text>
                </g>
                {isValid && (
                  <g>
                    <line x1={d2_px} y1={groundY} x2={d2_px} y2={groundY - personHeight * scale} stroke="#374151" strokeWidth="2" />
                    <circle cx={d2_px} cy={groundY - personHeight * scale} r="3" fill="#374151" />
                    <text x={d2_px} y={groundY - personHeight * scale - 8} fontSize="10" fill="#374151" textAnchor="middle">{t('personHeightLabel')}({formatLength(personHeight, unitSystem, 2)})</text>
                  </g>
                )}
                {isValid && D1 > 0 && (
                    <g>
                        <line x1={camX} y1={groundY + 20} x2={d1_px} y2={groundY + 20} stroke="#3b82f6" strokeWidth="1" markerEnd="url(#arrowhead-td1501)" markerStart="url(#arrowhead-td1501)"/>
                        <text x={camX + (d1_px - camX)/2} y={groundY + 33} fontSize="10" fill="#3b82f6" textAnchor="middle">D1: {formatLength(D1, unitSystem, 1)}</text>
                    </g>
                )}
                {isValid && D2 > 0 && (
                    <g>
                        <line x1={camX} y1={groundY + 45} x2={d2_px} y2={groundY + 45} stroke="#ef4444" strokeWidth="1" markerEnd="url(#arrowhead-td1501)" markerStart="url(#arrowhead-td1501)"/>
                        <text x={camX + (d2_px - camX)/2} y={groundY + 58} fontSize="10" fill="#ef4444" textAnchor="middle">D2: {formatLength(D2, unitSystem, 1)}</text>
                    </g>
                )}
            </svg>
        </div>
    );
};

const TopDownDiagram = ({ results }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!results || !results.summary || !results.summary.isValid) return null;

    if (results.isTopDown) {
        const vf = results.details?.visionField;
        const cf = results.details?.coverageField;
        if (!vf || !cf) return null;

        const width = 500, height = 400, padding = 40;
        
        const allPoints = [...vf.polygon, ...cf.polygon, {x:0, y:0}];
        const xCoords = allPoints.map(p => p.x);
        const yCoords = allPoints.map(p => p.y);
        const minX = Math.min(...xCoords);
        const maxX = Math.max(...xCoords);
        const minY = Math.min(...yCoords);
        const maxY = Math.max(...yCoords);
        
        const dataWidth = maxX - minX;
        const dataHeight = maxY - minY;
        const dataCenterX = (minX + maxX) / 2;
        const dataCenterY = (minY + maxY) / 2;
        
        const scale = Math.min( dataWidth > 0 ? (width - padding * 2) / dataWidth : Infinity, dataHeight > 0 ? (height - padding * 2) / dataHeight : Infinity );
        const toSvgCoord = (p) => ({ x: (width / 2) + (p.x - dataCenterX) * scale, y: (height / 2) - (p.y - dataCenterY) * scale, });
        
        const visionPolyPoints = vf.polygon.map(toSvgCoord).map(p => `${p.x},${p.y}`).join(' ');
        const coveragePolyPoints = cf.polygon.map(toSvgCoord).map(p => `${p.x},${p.y}`).join(' ');
        
        const coverageBbox = { 
            minX: Math.min(...cf.polygon.map(p => toSvgCoord(p).x)), 
            maxX: Math.max(...cf.polygon.map(p => toSvgCoord(p).x)), 
            minY: Math.min(...cf.polygon.map(p => toSvgCoord(p).y)), 
            maxY: Math.max(...cf.polygon.map(p => toSvgCoord(p).y))
        };
        const camPosSvg = toSvgCoord({x:0, y:0});

        return (
            <div className="bg-white p-6 rounded-lg shadow-lg h-full">
                <h2 className="text-xl font-bold mb-4 text-gray-700">{t('topDownView')} (X-Y)</h2>
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto border rounded-md">
                     <defs><marker id="arrowhead-td1502-top" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" /></marker></defs>
                    <polygon points={visionPolyPoints} fill="rgba(0, 180, 255, 0.1)" stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                    <polygon points={coveragePolyPoints} fill="rgba(22, 163, 74, 0.2)" stroke="rgba(22, 163, 74, 0.8)" strokeWidth="1" />
                    <circle cx={camPosSvg.x} cy={camPosSvg.y} r="5" fill="#0891b2" />
                    <text x={camPosSvg.x} y={camPosSvg.y - 10} fontSize="10" fill="#0891b2" textAnchor="middle">{t('cameraPosition')}</text>
                    <line x1={coverageBbox.minX} y1={coverageBbox.maxY + 15} x2={coverageBbox.maxX} y2={coverageBbox.maxY + 15} stroke="#3b82f6" strokeWidth="1" markerStart="url(#arrowhead-td1502-top)" markerEnd="url(#arrowhead-td1502-top)" />
                    <text x={(coverageBbox.minX + coverageBbox.maxX)/2} y={coverageBbox.maxY + 28} fontSize="10" fill="#3b82f6" textAnchor="middle">Effective Width: {formatLength(cf.aveWidth, unitSystem, 2)}</text>
                    <line x1={coverageBbox.maxX + 15} y1={coverageBbox.minY} x2={coverageBbox.maxX + 15} y2={coverageBbox.maxY} stroke="#ef4444" strokeWidth="1" markerStart="url(#arrowhead-td1502-top)" markerEnd="url(#arrowhead-td1502-top)" />
                    <text x={coverageBbox.maxX + 20} y={(coverageBbox.minY + coverageBbox.maxY)/2} fontSize="10" fill="#ef4444" textAnchor="start">Depth: {formatLength(cf.aveHeight, unitSystem, 2)}</text>
                </svg>
            </div>
        );
    }

    const { D1, D2, W1, W2, suggestD, suggestW } = results.summary;
    const width = 500;
    const height = 300;
    const padding = 50;
    
    const maxW = Math.max(W1, W2, 1);
    const maxD = D2;

    const availableWidth = width - padding * 2;
    const availableHeight = height - padding * 2;

    const scale = D2 > 0 ? Math.min(availableWidth / maxW, availableHeight / maxD) : 1;

    const centerX = width / 2;
    const bottomY = height - padding;

    const p1 = { x: centerX - (W1 / 2) * scale, y: bottomY - D1 * scale };
    const p2 = { x: centerX + (W1 / 2) * scale, y: bottomY - D1 * scale };
    const p3 = { x: centerX + (W2 / 2) * scale, y: bottomY - D2 * scale };
    const p4 = { x: centerX - (W2 / 2) * scale, y: bottomY - D2 * scale };
    
    const suggest_y = bottomY - suggestD * scale;
    const suggest_x1 = centerX - (suggestW / 2) * scale;
    const suggest_x2 = centerX + (suggestW / 2) * scale;

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg h-full">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('topDownView')}</h2>
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto border rounded-md">
                <defs>
                    <marker id="arrowhead-topdown-td1501" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
                    </marker>
                </defs>
                <circle cx={centerX} cy={bottomY} r="5" fill="#0891b2" />
                <text x={centerX} y={bottomY + 15} fontSize="10" textAnchor="middle">{t('cameraPosition')}</text>
                <polygon points={`${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y} ${p4.x},${p4.y}`} fill="rgba(22, 163, 74, 0.2)" stroke="rgba(22, 163, 74, 0.8)" strokeWidth="1" />
                <line x1={suggest_x1} y1={suggest_y} x2={suggest_x2} y2={suggest_y} stroke="#22c55e" strokeWidth="2" strokeDasharray="5,2" />
                <text x={suggest_x2 + 5} y={suggest_y} dy="0.35em" fontSize="10" fill="#22c55e" textAnchor="start">{t('suggestedPoint').split(' ')[1]}: {formatLength(suggestW, unitSystem, 1)}</text>
                <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#3b82f6" strokeWidth="1"/>
                <text x={centerX} y={p1.y + 15} fontSize="10" fill="#3b82f6" textAnchor="middle">W1: {formatLength(W1, unitSystem, 1)}</text>
                <line x1={p4.x} y1={p4.y} x2={p3.x} y2={p3.y} stroke="#ef4444" strokeWidth="1"/>
                <text x={centerX} y={p4.y - 5} fontSize="10" fill="#ef4444" textAnchor="middle">W2: {formatLength(W2, unitSystem, 1)}</text>
                <line x1={centerX} y1={bottomY} x2={centerX} y2={p4.y} stroke="#9ca3af" strokeWidth="1" strokeDasharray="2,2"/>
                <line x1={centerX - 10} y1={bottomY} x2={centerX - 10} y2={p1.y} stroke="#3b82f6" strokeWidth="1" markerStart="url(#arrowhead-topdown-td1501)" markerEnd="url(#arrowhead-topdown-td1501)"/>
                <text x={centerX - 15} y={bottomY - (D1/2)*scale} fontSize="10" fill="#3b82f6" textAnchor="end">D1: {formatLength(D1, unitSystem, 1)}</text>
                <line x1={centerX + 10} y1={bottomY} x2={centerX + 10} y2={p4.y} stroke="#ef4444" strokeWidth="1" markerStart="url(#arrowhead-topdown-td1501)" markerEnd="url(#arrowhead-topdown-td1501)"/>
                <text x={centerX + 15} y={bottomY - (D2/2)*scale} fontSize="10" fill="#ef4444" textAnchor="start">D2: {formatLength(D2, unitSystem, 1)}</text>
            </svg>
        </div>
    );
};

export const LENS_PRESETS = {
    "500W/3.6MM": { fovWidthAt3m: 4.59, fovHeightAt3m: 2.55, pixelWidth: 2592 },
    "500W/6MM": { fovWidthAt3m: 2.53, fovHeightAt3m: 1.52, pixelWidth: 2592 },
    "500W/12MM": { fovWidthAt3m: 1.266816104, fovHeightAt3m: 0.7125840585, pixelWidth: 2592, dofNearL: 7.5, dofFarL: 45 },
    "500W/16MM": { fovWidthAt3m: 0.946952883, fovHeightAt3m: 0.5326609966875, pixelWidth: 2592, dofNearL: 13, dofFarL: 60 },
};

export const LENS_PRESETS_TC = {
    "500W/2.2MM": { fovWidthAt3m: 8.4, fovHeightAt3m: 8.4 / 16 * 9, pixelWidth: 512 },
    "500W/3.6MM": { fovWidthAt3m: 4.59, fovHeightAt3m: 2.55, pixelWidth: 512 },
    "500W/6MM": { fovWidthAt3m: 2.53, fovHeightAt3m: 1.52, pixelWidth: 512 },
    "500W/8MM": { fovWidthAt3m: 2.38, fovHeightAt3m: 2.38 / 16 * 9, pixelWidth: 512 },
    "500W/12MM": { fovWidthAt3m: 1.33, fovHeightAt3m: 1.33 / 16 * 9, pixelWidth: 512 },
    "500W/16MM": { fovWidthAt3m: 0.9, fovHeightAt3m: 0.9 / 16 * 9, pixelWidth: 512 },
    "500W/25MM": { fovWidthAt3m: 0.57, fovHeightAt3m: 0.57 / 16 * 9, pixelWidth: 512 },
};

export const LENS_MODES = {
    "500W/3.6MM": {
        "Standard": { heightRange: [2, 5] },
        "Wide Field": { heightRange: [2, 7] },
        "Top-Down": { heightRange: [2, 7] },
    },
    "500W/6MM": {
        "Standard": { heightRange: [2, 8] },
        "Wide Field": { heightRange: [2, 12] },
        "Top-Down": { heightRange: [2, 12] },
    },
    "500W/12MM": {
        "Standard": { heightRange: [2, 15] },
        "Top-Down": { heightRange: [2, 15] },
    },
    "500W/16MM": {
        "Standard": { heightRange: [2, 26] },
        "Top-Down": { heightRange: [2, 26] },
    }
};

export const LENS_MODES_TC = {
    "500W/2.2MM": {
        "Standard": { heightRange: [1.5, 10] },
    },
    "500W/3.6MM": {
        "Standard": { heightRange: [1.5, 10] },
    },
    "500W/6MM": {
        "Standard": { heightRange: [1.5, 10] },
    },
    "500W/8MM": {
        "Standard": { heightRange: [1.5, 10] },
    },
    "500W/12MM": {
        "Standard": { heightRange: [1.5, 10] },
    },
    "500W/16MM": {
        "Standard": { heightRange: [1.5, 10] },
    },
    "500W/25MM": {
        "Standard": { heightRange: [1.5, 10] },
    },
};

const PARAMS_STORAGE_KEY = 'td1501-admin-params';

const getMergedParams = (lensType, mode, countingMethod = 'ReID') => {
    const isTC = countingMethod === 'Traffic Only';
    const defaultParams = { ...defaultConfig };
    
    if (isTC) {
        // TC fixed parameters
        defaultParams.personHeight = 1.75;
        defaultParams.minPixel = 20;
        defaultParams.maxTiltAngle = 80;
        defaultParams.personEntryCorrection = 0.4;
        defaultParams.minCoverageDepth = 0.5;
        defaultParams.recommendedPixel = 32;
    } else {
        // ReID: adjust params based on vision mode
        if (mode === 'Standard') {
            defaultParams.minPixel = 80;
            defaultParams.maxTiltAngle = 45;
        } else { // Wide Field
            defaultParams.minPixel = 64;
            defaultParams.maxTiltAngle = 60;
        }
    }
    
    const presetsSource = isTC ? LENS_PRESETS_TC : LENS_PRESETS;
    const lensProps = presetsSource[lensType];
    if (!lensProps) return defaultParams;

    let finalParams = {
        ...defaultParams,
        pixelWidth: lensProps.pixelWidth,
        fovWidthAt3m: lensProps.fovWidthAt3m,
        fovHeightAt3m: lensProps.fovHeightAt3m,
        dofNearL: lensProps.dofNearL,
        dofFarL: lensProps.dofFarL,
    };

    try {
        const storedParams = localStorage.getItem(PARAMS_STORAGE_KEY);
        if (storedParams && !isTC) {
            const parsedStored = JSON.parse(storedParams);
            finalParams = { ...finalParams, ...parsedStored, minPixel: defaultParams.minPixel, maxTiltAngle: defaultParams.maxTiltAngle };
        }
    } catch (error) {
        console.error("Failed to parse params from localStorage", error);
    }
    return finalParams;
};

export default function TD1501Calculator({ isAdmin }) {
  const { t, language } = useTranslation();
  const [inputs, setInputs] = useState({
    installationHeight: 3.0,
    tiltingAngle: 15,
    lensType: "500W/3.6MM",
    personHeight: defaultConfig.personHeight || 1.6,
    mode: "Standard",
    countingMethod: "ReID",
    xTilt: 0,
    yTilt: 0,
  });

  const [params, setParams] = useState(() => getMergedParams(inputs.lensType, inputs.mode, inputs.countingMethod));
  
  useEffect(() => {
    setParams(getMergedParams(inputs.lensType, inputs.mode, inputs.countingMethod));
    // When lens, mode, or counting method changes, reset person height to the config default
    setInputs(prev => ({...prev, personHeight: getMergedParams(inputs.lensType, inputs.mode, inputs.countingMethod).personHeight }));
  }, [inputs.lensType, inputs.mode, inputs.countingMethod, language]);

  useEffect(() => {
    if (isAdmin) {
      setInputs(prevInputs => {
        if (prevInputs.personHeight !== params.personHeight) {
          return { ...prevInputs, personHeight: params.personHeight };
        }
        return prevInputs;
      });
    }
  }, [params.personHeight, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      setParams(prevParams => {
        if (Math.abs(prevParams.personHeight - inputs.personHeight) > 0.001) {
          return { ...prevParams, personHeight: inputs.personHeight };
        }
        return prevParams;
      });
    }
  }, [inputs.personHeight, isAdmin]);

  const handleResetParams = () => {
      localStorage.removeItem(PARAMS_STORAGE_KEY);
      const newParams = getMergedParams(inputs.lensType, inputs.mode, inputs.countingMethod);
      setParams(newParams);
      setInputs(prev => ({...prev, personHeight: newParams.personHeight}));
  };

  const optimalAngle = useMemo(() => {
    const inputsForOptimalCalc = { installationHeight: inputs.installationHeight, personHeight: inputs.personHeight, mode: inputs.mode, countingMethod: inputs.countingMethod };
    return findOptimalAngle(inputsForOptimalCalc, params, t);
  }, [inputs.installationHeight, inputs.personHeight, inputs.mode, inputs.countingMethod, params, t]);

  const maxValidAngle = useMemo(() => {
    const inputsForMaxAngleCalc = { installationHeight: inputs.installationHeight, personHeight: inputs.personHeight, mode: inputs.mode, countingMethod: inputs.countingMethod };
    return findMaxValidAngle(inputsForMaxAngleCalc, params, t, optimalAngle);
  }, [inputs.installationHeight, inputs.personHeight, inputs.mode, inputs.countingMethod, params, t, optimalAngle]);

  const dynamicHeightBounds = useMemo(() => {
    if (inputs.mode === 'Top-Down') return undefined; // Let Top-Down use its static bounds

    const isTC = inputs.countingMethod === 'Traffic Only';
    const activeLensModes = isTC ? LENS_MODES_TC : LENS_MODES;
    const currentModeOptions = activeLensModes[inputs.lensType] || {};
    const selectedModeConfig = currentModeOptions[inputs.mode] || { heightRange: [1.5, 10] };
    const [minH, maxH] = selectedModeConfig.heightRange;

    let validMin = minH;
    let validMax = maxH;
    
    // Function to check if a height has ANY valid angle
    const hasValidAngle = (h) => {
        const tempInputs = { 
            installationHeight: h,
            personHeight: inputs.personHeight,
            lensType: inputs.lensType,
            mode: inputs.mode,
            countingMethod: inputs.countingMethod,
            xTilt: inputs.xTilt,
            yTilt: inputs.yTilt,
            tiltingAngle: 0 
        };
        // We only need to check angles that are likely to be valid, 0 to 90
        for(let angle = 0; angle <= 90; angle += 2) {
            tempInputs.tiltingAngle = angle;
            const result = calculateTd1501Coverage(tempInputs, params, t);
            if (result?.summary?.isValid) return true;
        }
        return false;
    };

    // Find min valid height
    for (let h = minH; h <= maxH; h += 0.1) {
        if (hasValidAngle(h)) {
            validMin = h;
            break;
        }
    }

    // Find max valid height
    for (let h = maxH; h >= minH; h -= 0.1) {
        if (hasValidAngle(h)) {
            validMax = h;
            break;
        }
    }

    if (validMax < validMin) return [minH, maxH];
    
    // Format to 1 decimal place to prevent floating point issues
    return [Math.round(validMin * 10) / 10, Math.round(validMax * 10) / 10];
  }, [inputs.lensType, inputs.mode, inputs.countingMethod, inputs.personHeight, inputs.xTilt, inputs.yTilt, params, t]);

  useEffect(() => {
    setInputs(prevInputs => {
        let newInputs = { ...prevInputs, tiltingAngle: optimalAngle };
        if (dynamicHeightBounds) {
            const [minH, maxH] = dynamicHeightBounds;
            if (newInputs.installationHeight < minH) newInputs.installationHeight = minH;
            if (newInputs.installationHeight > maxH) newInputs.installationHeight = maxH;
        }
        return newInputs;
    });
  }, [optimalAngle, dynamicHeightBounds]);

  const calculationResults = useMemo(() => {
    return calculateTd1501Coverage(inputs, params, t);
  }, [inputs, params, t]);

  if (calculationResults.error) {
    return (
        <div className="p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-lg shadow-lg">
            <p className="font-bold">{t('calculationError')}</p>
            <p>{calculationResults.error}</p>
        </div>
    );
  }

  if (isAdmin) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 space-y-8">
                <Controls 
                    inputs={inputs} 
                    setInputs={setInputs} 
                    lensPresets={LENS_PRESETS} 
                    lensPresetsTC={LENS_PRESETS_TC}
                    lensModes={LENS_MODES}
                    lensModesTC={LENS_MODES_TC}
                    optimalAngle={optimalAngle}
                    maxValidAngle={maxValidAngle}
                    isAdmin={isAdmin}
                    dynamicHeightBounds={dynamicHeightBounds}
                />
                <ParametersAndDerivedPanel 
                    data={calculationResults} 
                    params={params} 
                    setParams={setParams} 
                    onReset={handleResetParams}
                    isAdmin={isAdmin}
                />
            </div>
            <div className="lg:col-span-2 space-y-8">
                <SummaryResults data={calculationResults} />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <CoverageDiagram inputs={inputs} results={calculationResults} params={params} />
                    <TopDownDiagram results={calculationResults} />
                </div>
                <DetailedResultsTable data={calculationResults} />
            </div>
        </div>
    );
  } else {
    return (
        <div className="flex flex-col gap-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1">
                    <Controls
                        inputs={inputs}
                        setInputs={setInputs}
                        lensPresets={LENS_PRESETS}
                        lensPresetsTC={LENS_PRESETS_TC}
                        lensModes={LENS_MODES}
                        lensModesTC={LENS_MODES_TC}
                        optimalAngle={optimalAngle}
                        maxValidAngle={maxValidAngle}
                        isAdmin={isAdmin}
                        dynamicHeightBounds={dynamicHeightBounds}
                    />
                </div>
                <div className="lg:col-span-2">
                    <SummaryResults data={calculationResults} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <CoverageDiagram inputs={inputs} results={calculationResults} params={params} />
                <TopDownDiagram results={calculationResults} />
            </div>
        </div>
    );
  }
}
// 功能描述: TD1501型号客流摄像机的覆盖范围计算器，并导出其核心计算逻辑。
